import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  createFilterOptions,
  MenuItem,
  Dialog,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  AddIconBox,
  AddIconContainerBox,
  classes,
  Root,
  VersionActionButtonsContainer,
} from './utils';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import checkCircle from '../../../../assets/common-assets/check-circle-green.svg';
import ConfirmDialog from '../../utils/ConfirmDialog';
import {
  addVersion,
  specificVersionValidate,
} from '../../../../services/versionApi';
import { exportJSON, exportXML } from '../../../../services/allInvoiceApi';
import { SelectStageMenu } from '../document-tabs/utils';
import { ANNOTATION } from '../../../../constants/staticContent';
import Toast from '../../utils/Toast';

const ConfirmDocument = ({
  CloseEditInfo,
  invoiceId,
  annotationData,
  mappedResult,
  exportDocumentOpen,
  invoiceVersions,
  setIsLoading,
  setIsVersionAdded,
  changedData,
  saveAndExitOpen,
  setSaveAndExitOpen,
  exportToJsonOpen,
  exportToXmlOpen,
  setExportToJsonOpen,
  setExportToXmlOpen,
  saveOpen,
  setSaveOpen,
  open,
}) => {
  const location = useLocation();
  const [version, setVersion] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versionId, setVersionId] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [isNewVersion, setIsNewVersion] = useState(false);
  const [checkEmptyValue, setCheckEmptyValue] = useState(false);
  const [isChangesDetected, setIsChangesDetected] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState('Save & Export');
  const [anchorEl, setAnchorEl] = useState(null);
  const [versions, setVersions] = useState([]);

  const filter = createFilterOptions();

  const navigate = useNavigate();

  const getCurrentPath = () => {
    let navigateTo;
    switch (true) {
      case location?.pathname.includes('invoice'):
        navigateTo = '/invoices';
        break;
      case location?.pathname.includes('sale'):
        navigateTo = '/sales';
        break;
      case location?.pathname.includes('receipt'):
        navigateTo = '/receipts';
        break;
      case location?.pathname.includes('creditcardstatement'):
        navigateTo = '/creditcardstatements';
        break;
      case location?.pathname.includes('bank-statement'):
        navigateTo = '/bank-statements';
        break;
      default:
        navigateTo = '/home'; // Optional: default route
    }
    return navigateTo;
  };
  const handleButtonClick = async () => {
    const versionList = invoiceVersions.map((obj) => obj.version);
    if (selectedVersion !== '') {
      if (changedData.length === 0) {
        Toast({
          message: ANNOTATION.INVOICE_ANNOTATION_MESSAGE.NO_CHANGES_DETECTED,
          variant: 'warning',
        });
        setIsChangesDetected(false);
      }
      if (versionList.includes(selectedVersion) && selectedVersion !== '') {
        setSaveAndExitOpen(true);
        setCheckEmptyValue(false);
        const getVersionId = invoiceVersions.filter(
          (item) => item.version === selectedVersion,
        );
        setVersionId(getVersionId[0].id);
      } else if (changedData.length > 0 && isNewVersion) {
        await addVersion(invoiceId, mappedResult, annotationData, newVersion);
        setSaveAndExitOpen(false);
        navigate(getCurrentPath());
      }
    } else {
      setCheckEmptyValue(true);
      setIsChangesDetected(false);
    }
  };

  const handleSave = async () => {
    const versionList = invoiceVersions.map((obj) => obj.version);
    if (selectedVersion !== '') {
      if (versionList.includes(selectedVersion) && selectedVersion !== '') {
        setSaveOpen(true);
        const getVersionId = invoiceVersions.filter(
          (item) => item.version === selectedVersion,
        );
        setVersionId(getVersionId[0].id);
        if (changedData.length === 0) {
          Toast({
            message: ANNOTATION.INVOICE_ANNOTATION_MESSAGE.NO_CHANGES_DETECTED,
            variant: 'warning',
          });
          setIsChangesDetected(false);
        }
      } else if (changedData.length > 0 && isNewVersion) {
        setIsLoading(true);
        await addVersion(invoiceId, mappedResult, annotationData, newVersion);
        setIsVersionAdded((prevValue) => !prevValue);
        setSaveOpen(false);
        CloseEditInfo();
        setCheckEmptyValue(false);
        setIsLoading(false);
      } else if (changedData.length === 0 && isNewVersion) {
        setIsChangesDetected(false);
      }
    } else {
      setCheckEmptyValue(true);
    }
  };

  const handleConfirmationSaveOK = () => {
    setSaveOpen(true);
  };
  const handleConfirmationExportToJsonOK = () => {
    setExportToJsonOpen(true);
  };
  const handleConfirmationExportToXmlOK = () => {
    setExportToXmlOpen(true);
  };
  const handleConfirmationExportToJsonNo = () => {
    setExportToJsonOpen(false);
  };
  const handleConfirmationExportToXmlNo = () => {
    setExportToXmlOpen(false);
  };
  const handleConfirmationSaveAndExitOK = () => {
    setSaveAndExitOpen(true);
  };
  const handleConfirmationSaveNo = () => {
    setSaveOpen(false);
  };
  const handleConfirmationSaveAndExitNo = () => {
    setSaveAndExitOpen(false);
  };
  const handleConfirmAction = async () => {
    const confirmHandle = await specificVersionValidate(
      annotationData,
      invoiceId,
      versionId,
    );
    if (confirmHandle) {
      navigate(getCurrentPath());
      setSaveAndExitOpen(false);
      setVersion('');
      setSelectedVersion('');
      CloseEditInfo();
    }
  };

  const handleExportToJsonAction = async () => {
    const confirmHandle = await specificVersionValidate(
      annotationData,
      invoiceId,
      versionId,
    );
    if (confirmHandle) {
      const jsonData = await exportJSON(versionId);
      const documentName = jsonData.data.jsonData.fileName;
      const jsonString = JSON.stringify(jsonData.data.jsonData.jsonData);
      const BloB = new Blob([jsonString], {
        type: 'text/plain;charset=utf-8',
      });
      const Url = URL.createObjectURL(BloB);
      const link = document.createElement('a');
      link.href = Url;
      link.download = documentName;
      link.click();
      URL.revokeObjectURL(Url); // Clean up the temporary URL
      navigate(getCurrentPath());
      setExportToJsonOpen(false);
      setVersion('');
      setSelectedVersion('');
      CloseEditInfo();
    }
  };
  const handleExportToXmlAction = async () => {
    const confirmHandle = await specificVersionValidate(
      annotationData,
      invoiceId,
      versionId,
    );
    if (confirmHandle) {
      const fileData = await exportXML(versionId);
      const { xmlData } = fileData;
      const { fileName } = fileData;
      if (xmlData) {
        const blob = new Blob([xmlData], {
          type: 'application/xml; charset=utf-8',
        });

        // Create a download link or trigger a download automatically
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();

        // Revoke the object URL to avoid memory leaks
        URL.revokeObjectURL(link.href);
      }
      navigate(getCurrentPath());
      setExportToXmlOpen(false);
      setVersion('');
      setSelectedVersion('');
      CloseEditInfo();
    }
  };

  const handleSaveAction = async () => {
    setIsLoading(true);
    await specificVersionValidate(annotationData, invoiceId, versionId);
    setSaveOpen(false);
    setVersion('');
    setSelectedVersion('');
    CloseEditInfo();
    setSelectedVersion('');
    setIsLoading(false);
  };

  const handleAddValue = async (newValue) => {
    if (newValue?.id) {
      setIsNewVersion(false);
    } else {
      setIsNewVersion(true);
    }
    if (!newValue) {
      setSelectedVersion('');
      setVersion('');
      setSaveOpen(false);
      setExportToJsonOpen(false);
      setExportToXmlOpen(false);
      setSaveAndExitOpen(false);
    }
    if (newValue !== null && newValue.inputValue) {
      const newVersionValue = newValue.inputValue;
      setNewVersion(newVersionValue);
      setSelectedVersion(newVersionValue);
      setVersion(newVersionValue);
      setCheckEmptyValue(false);
    } else if (newValue !== null && newValue.version) {
      setSelectedVersion(newValue.version);
      setVersion(newValue.version);
      setCheckEmptyValue(false);
    }
  };

  useEffect(() => {
    if (changedData.length > 0) {
      setIsChangesDetected(true);
    }
  }, [changedData]);

  useEffect(() => {
    if (exportDocumentOpen) {
      if (invoiceVersions) {
        setVersions(invoiceVersions);
      }
    } else {
      setVersions([]);
      setVersion('');
      setSelectedVersion('');
      setCheckEmptyValue(false);
    }
  }, [exportDocumentOpen]);

  const stageOptions = ['Export to JSON', 'Export to XML'];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async (format) => {
    setAnchorEl(null);
    if (format) {
      const versionList = invoiceVersions.map((obj) => obj.version);
      switch (format) {
        case 'Export to Excel':
          if (selectedVersion !== '') {
            if (
              versionList.includes(selectedVersion) &&
              selectedVersion !== ''
            ) {
              const getVersionId = invoiceVersions.filter(
                (item) => item.version === selectedVersion,
              );
              setVersionId(getVersionId[0].id);
              if (changedData.length === 0) {
                setIsChangesDetected(false);
              }
            } else if (changedData.length > 0 && isNewVersion) {
              setCheckEmptyValue(false);
              Toast({
                message:
                  ANNOTATION.INVOICE_ANNOTATION_MESSAGE
                    .EXPORT_VALIDATION_MESSAGE,
                variant: 'error',
              });
            } else if (changedData.length === 0 && isNewVersion) {
              setIsChangesDetected(false);
            }
          } else {
            setCheckEmptyValue(true);
          }
          break;
        case 'Export to JSON':
          if (selectedVersion !== '') {
            if (
              versionList.includes(selectedVersion) &&
              selectedVersion !== ''
            ) {
              setExportToJsonOpen(true);
              const getVersionId = invoiceVersions.filter(
                (item) => item.version === selectedVersion,
              );
              setVersionId(getVersionId[0].id);
              if (changedData.length === 0) {
                setIsChangesDetected(false);
              }
            } else if (changedData.length > 0 && isNewVersion) {
              setExportToJsonOpen(false);
              setCheckEmptyValue(false);
              Toast({
                message:
                  ANNOTATION.INVOICE_ANNOTATION_MESSAGE
                    .EXPORT_VALIDATION_MESSAGE,
                variant: 'error',
              });
            } else if (changedData.length === 0 && isNewVersion) {
              setIsChangesDetected(false);
            }
          } else {
            setCheckEmptyValue(true);
          }
          break;
        case 'Export to XML':
          if (selectedVersion !== '') {
            if (
              versionList.includes(selectedVersion) &&
              selectedVersion !== ''
            ) {
              setExportToXmlOpen(true);
              const getVersionId = invoiceVersions.filter(
                (item) => item.version === selectedVersion,
              );
              setVersionId(getVersionId[0].id);
              if (changedData.length === 0) {
                setIsChangesDetected(false);
              }
            } else if (changedData.length > 0 && isNewVersion) {
              setExportToXmlOpen(false);
              setCheckEmptyValue(false);
              Toast({
                message:
                  ANNOTATION.INVOICE_ANNOTATION_MESSAGE
                    .EXPORT_VALIDATION_MESSAGE,
                variant: 'error',
              });
            } else if (changedData.length === 0 && isNewVersion) {
              setIsChangesDetected(false);
            }
          } else {
            setCheckEmptyValue(true);
          }
          break;
        default:
          break;
      }
      setSelectedFormat(format);
    }
  };

  return (
    <Dialog open={open} onClose={CloseEditInfo}>
      <Root className={classes.root}>
        <Card className={classes.cardComponent}>
          <CardHeader
            className={classes.cardHeader}
            title={
              <Box>
                <Grid item xs={12} container justifyContent="flex-end">
                  <Avatar
                    onClick={CloseEditInfo}
                    src={closeIcon}
                    className={classes.closeAction}
                  />
                </Grid>
                <Grid item xs={12} container justifyContent="center">
                  <Box
                    component="img"
                    src={checkCircle}
                    alt="check-circle"
                    className={classes.ConfirmDocumentCheckCircleIcon}
                  />
                </Grid>
                <Grid item xs={12} container justifyContent="center">
                  <Typography variant="h4">
                    {ANNOTATION.REVIEW_DOCUMENT}
                  </Typography>
                </Grid>
              </Box>
            }
          />
          <CardContent className={classes.cardContent}>
            <Box className={classes.PreviousVersionBox}>
              <Grid item xs={12}>
                <Box className={classes.gridItems}>
                  <Typography
                    variant="h5"
                    className={classes.gridItemsTypography}
                  >
                    Current Version:
                  </Typography>
                  <Typography variant="h4" className={classes.previousVersion}>
                    {versions[0]?.version}
                  </Typography>
                </Box>
              </Grid>
            </Box>
            <Box className={classes.VersionAutoCompleteBox}>
              <Box className={classes.gridItems}>
                <Typography
                  variant="h5"
                  className={classes.gridItemsTypography}
                >
                  Specify the Version to Save Changes:
                </Typography>
                <Autocomplete
                  className={classes.gridItemAutoComplete}
                  value={version}
                  limitTags={2}
                  isOptionEqualToValue={(option, newValue) => {
                    if (version.inputValue) {
                      return option.version === newValue.inputValue;
                    }
                    return option.id === newValue.id;
                  }}
                  ListboxProps={{ className: 'Autocomplete_scrollbar' }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const modifiedEvent = { ...event };
                      modifiedEvent.defaultMuiPrevented = true;
                    }
                  }}
                  onChange={(event, newValue) => {
                    handleAddValue(newValue);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    const isExisting = options.some(
                      (option) => inputValue === option.version,
                    );
                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        inputValue,
                        version: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  options={versions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option.version;
                  }}
                  renderOption={(props, option) => (
                    <li key={option.id} {...props}>
                      {option.inputValue ? (
                        <AddIconContainerBox>
                          <AddIconBox>
                            <AddIcon />
                          </AddIconBox>
                          Add &ldquo;
                          {option.inputValue}
                          &rdquo;
                        </AddIconContainerBox>
                      ) : (
                        <Typography variant="body2">
                          {option.version}
                        </Typography>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select version or Add new version"
                      size="small"
                    />
                  )}
                />
                {checkEmptyValue && !isChangesDetected ? (
                  <Typography
                    variant="body1"
                    className={classes.selectVersionHelperText}
                  >
                    {
                      ANNOTATION.INVOICE_ANNOTATION_MESSAGE
                        .SELECT_VERSION_VALIDATION_MESSAGE
                    }
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <VersionActionButtonsContainer>
              <Box>
                <Button
                  variant="contained"
                  className={classes.SaveChangesButtons}
                  onClick={handleSave}
                >
                  <Typography variant="body1">Save</Typography>
                </Button>
                {saveOpen && (changedData.length > 0 || isNewVersion) ? (
                  <ConfirmDialog
                    open={handleConfirmationSaveOK}
                    onClose={handleConfirmationSaveNo}
                    askingMessage={
                      ANNOTATION.CONFORM_DOCUMENT.SAVE_CHANGES_EXISTING_VERSION
                    }
                    onConfirm={handleSaveAction}
                    isStatusCompleted={undefined}
                  />
                ) : null}
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={handleClick}
                  className={classes.stagSelectDropdownButton}
                >
                  {selectedFormat}
                  <KeyboardArrowDownIcon
                    className={
                      anchorEl
                        ? classes.arrowDownIconWithTransition
                        : classes.arrowDownIconWithoutTransition
                    }
                  />
                </Button>
                <SelectStageMenu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => handleClose()}
                >
                  {stageOptions.map((option) => (
                    <MenuItem key={option} onClick={() => handleClose(option)}>
                      {option}
                    </MenuItem>
                  ))}
                </SelectStageMenu>
                {exportToJsonOpen &&
                (changedData.length > 0 || isNewVersion) ? (
                  <ConfirmDialog
                    open={handleConfirmationExportToJsonOK}
                    onClose={handleConfirmationExportToJsonNo}
                    askingMessage={
                      ANNOTATION.CONFORM_DOCUMENT.SAVE_CHANGES_EXISTING_VERSION
                    }
                    onConfirm={handleExportToJsonAction}
                  />
                ) : null}
                {exportToXmlOpen && (changedData.length > 0 || isNewVersion) ? (
                  <ConfirmDialog
                    open={handleConfirmationExportToXmlOK}
                    onClose={handleConfirmationExportToXmlNo}
                    askingMessage={
                      ANNOTATION.CONFORM_DOCUMENT.SAVE_CHANGES_EXISTING_VERSION
                    }
                    onConfirm={handleExportToXmlAction}
                  />
                ) : null}
              </Box>
              <Box>
                <Button
                  variant="contained"
                  className={classes.SaveChangesButtons}
                  onClick={handleButtonClick}
                >
                  Save & Exit
                </Button>
                {saveAndExitOpen && (changedData.length > 0 || isNewVersion) ? (
                  <ConfirmDialog
                    open={handleConfirmationSaveAndExitOK}
                    onClose={handleConfirmationSaveAndExitNo}
                    askingMessage={
                      ANNOTATION.CONFORM_DOCUMENT.SAVE_CHANGES_EXISTING_VERSION
                    }
                    onConfirm={handleConfirmAction}
                  />
                ) : null}
              </Box>
            </VersionActionButtonsContainer>
          </CardContent>
        </Card>
      </Root>
    </Dialog>
  );
};

const AnnotationDataPropTypes = PropTypes.shape({
  x: PropTypes.number,
  y: PropTypes.number,
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  section: PropTypes.string,
  version: PropTypes.string,
  validated: PropTypes.bool,
  short_desc: PropTypes.string,
});

const MappedResultPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  short_desc: PropTypes.string.isRequired,
});

const VersionDataPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
});

ConfirmDocument.defaultProps = {
  annotationData: [
    {
      label: undefined,
      value: undefined,
      short_desc: undefined,
      x: undefined,
      y: undefined,
      id: undefined,
      width: undefined,
      height: undefined,
    },
  ],
  data: [
    {
      id: undefined,
      x: undefined,
      y: undefined,
      width: undefined,
      height: undefined,
    },
  ],
};

ConfirmDocument.propTypes = {
  CloseEditInfo: PropTypes.func.isRequired,
  getInvoiceVersion: PropTypes.func.isRequired,
  setIsVersionAdded: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setSaveAndExitOpen: PropTypes.func.isRequired,
  setExportToJsonOpen: PropTypes.func.isRequired,
  setExportToXmlOpen: PropTypes.func.isRequired,
  setSaveOpen: PropTypes.func.isRequired,
  invoiceId: PropTypes.string.isRequired,
  annotationData: PropTypes.arrayOf(AnnotationDataPropTypes),
  mappedResult: PropTypes.arrayOf(MappedResultPropTypes).isRequired,
  documentUrl: PropTypes.string.isRequired,
  exportDocumentOpen: PropTypes.bool.isRequired,
  saveAndExitOpen: PropTypes.bool.isRequired,
  exportToJsonOpen: PropTypes.bool.isRequired,
  exportToXmlOpen: PropTypes.bool.isRequired,
  saveOpen: PropTypes.bool.isRequired,
  invoiceVersions: PropTypes.arrayOf(VersionDataPropTypes).isRequired,
  changedData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ).isRequired,
  open: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ),
};

export default ConfirmDocument;
